import {ColumnDef} from '@tanstack/react-table';
import {ReadLead} from '@tinct/core-api-models';
import {usePatchLead} from '@tinct/core-api';
import {toast} from 'sonner';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  LongText,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tinct/web-components';
import {CollectionColumnHeader, CollectionRowActions} from '@tinct/web-collection';
import {cn} from '@tinct/core-utils';
import {IconCopy, IconX} from '@tabler/icons-react';

const tagConfig: Record<
  string,
  { label: string; icon: string; className: string }
> = {
  hot: {
    label: 'Hot',
    icon: '🔥',
    className: 'bg-orange-100 text-orange-700 border-orange-200',
  },
  warm: {
    label: 'Warm',
    icon: '🌡️',
    className: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  cold: {
    label: 'Cold',
    icon: '❄️',
    className: 'bg-blue-100 text-blue-700 border-blue-200',
  },
};

type UseAudienceLeadsColumnsOptions = {
  onRemoveLead?: (lead: ReadLead) => void;
};

export function useAudienceLeadsColumns(
  options: UseAudienceLeadsColumnsOptions = {},
): ColumnDef<ReadLead>[] {
  const { onRemoveLead } = options;
  const { mutate: patchLead } = usePatchLead();

  return [
    {
      id: 'companyName',
      accessorFn: (row) => row.company?.name,
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Company" />
      ),
      cell: ({ row }) => {
        const company = row.original.company;
        const name = company?.name ?? '-';
        const logoUrl = company?.logo?.url;
        const initials = name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .slice(0, 2)
          .toUpperCase();

        return (
          <div className="flex items-center gap-3">
            <Avatar className="size-8">
              {logoUrl && <AvatarImage src={logoUrl} alt={name} />}
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <LongText className="max-w-[200px] font-semibold">{name}</LongText>
          </div>
        );
      },
      meta: { className: 'min-w-[250px]' },
      enableHiding: false
    },
    {
      accessorKey: 'industry',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Industry" />
      ),
      cell: ({ row }) => {
        const industry = row.original.company?.industry;
        return <div className="w-fit text-nowrap">{industry ?? '-'}</div>;
      },
      enableHiding: false
    },
    {
      id: 'enrichmentScore',
      accessorFn: (row) => row.company?.enrichmentScore,
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Enrichment" />
      ),
      cell: ({ row }) => {
        const progress = row.original.company?.enrichmentScore ?? 0;
        return (
          <div className="flex items-center gap-3 min-w-[120px]">
            <Progress value={progress} className="h-2 flex-1" />
            <span className="text-sm text-muted-foreground w-10 text-right">
              {progress}%
            </span>
          </div>
        );
      },
      meta: { className: 'min-w-[150px]' },
    },
    {
      accessorKey: 'tag',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Tag" />
      ),
      cell: ({ row }) => {
        const tag = row.original.tag;
        const leadId = row.original.id;

        const handleTagChange = (value: string) => {
          if (leadId) {
            patchLead({ id: leadId, data: { tag: value === 'none' ? null : value } });
          }
        };

        const config = tag ? tagConfig[tag] : undefined;

        return (
          <Select value={tag ?? 'none'} onValueChange={handleTagChange}>
            <SelectTrigger className={cn('w-[100px] h-7', config ? 'border-0 p-0' : 'text-xs')}>
              {config ? (
                <Badge
                  variant="outline"
                  className={cn('gap-1 cursor-pointer', config.className)}
                >
                  {config.icon}
                  {config.label}
                </Badge>
              ) : (
                <SelectValue placeholder="-" />
              )}
            </SelectTrigger>
            <SelectContent>
              {Object.entries(tagConfig).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-1">
                    {cfg.icon}
                    {cfg.label}
                  </span>
                </SelectItem>
              ))}
              <SelectItem value="none">
                <span className="flex items-center gap-1">
                  <span className="size-3 text-center">-</span>
                  No tag
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'pages',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Campaigns" />
      ),
      cell: ({ row }) => {
        const count = row.original.pages?.filter(
          (page) => page.status !== 'excluded_conflict',
        ).length ?? 0;
        return <div className="text-center">{count}</div>;
      },
      meta: { className: 'text-center' },
      enableSorting: false,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <CollectionRowActions
          row={row}
          actions={[
            {
              label: 'Copy Company ID',
              icon: IconCopy,
              onClick: (lead: ReadLead) => {
                const companyId = lead.company?.id;
                if (companyId) {
                  navigator.clipboard.writeText(companyId);
                  toast.success('Company ID copied to clipboard');
                }
              },
            },
            {
              label: 'Remove from audience',
              icon: IconX,
              group: 'danger',
              onClick: (lead: ReadLead) => {
                onRemoveLead?.(lead);
              },
            },
          ]}
        />
      ),
    },
  ];
}
